import React, { Fragment } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';
import { styled } from '@compiled/react';

import ChevronRightIcon from '@atlaskit/icon/core/chevron-right';
import ChevronLeftIcon from '@atlaskit/icon/core/chevron-left';
import { token } from '@atlaskit/tokens';

import type { OnboardingSlide } from './OnboardingModal.tsx';
import { ModalImage } from './ModalImage.tsx';

const i18n = defineMessages({
	imageAltText: {
		id: 'premium-onboarding.modal.image.alt-text',
		description: 'Alt text for onboarding modal images',
		defaultMessage: 'Feature illustration for this onboarding step',
	},
});

// Global constants
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const StyledBody = styled.div({
	textAlign: 'center',
	padding: token('space.600'),
	height: '100px',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const StyledImageWrapper = styled.div<{
	isTransparent?: boolean;
}>({
	width: '100%',
	height: '225px',
	maxHeight: '225px',
	position: 'relative',
	// eslint-disable-next-line @atlaskit/design-system/no-unsafe-design-token-usage
	borderTopLeftRadius: token('radius.small', '3px'),
	// eslint-disable-next-line @atlaskit/design-system/no-unsafe-design-token-usage
	borderTopRightRadius: token('radius.small', '3px'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	backgroundColor: (props) =>
		props.isTransparent ? 'transparent' : `${token('color.background.accent.teal.subtle')}`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const OldStyledChevronButton = styled.button({
	width: '30px',
	cursor: 'pointer',
	background: 0,
	padding: 0,
	outline: 0,
	border: 0,
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
	marginTop: '100px',
	opacity: 0.7,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const StyledChevronButton = styled(OldStyledChevronButton)({
	height: '30px',
	borderRadius: token('radius.full'),
	backgroundColor: token('color.background.accent.gray.bolder'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const StyledFlexedButtons = styled.div({
	height: '100%',
	display: 'flex',
	flexDirection: 'row',
	padding: `${token('space.0')} ${token('space.200')}`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const StyledButtonLeft = styled.div({
	textAlign: 'left',
	flex: 1,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const StyledButtonRight = styled.div({
	textAlign: 'right',
	flex: 1,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const StyledHeading = styled.h2({
	font: token('font.heading.medium'),
	marginBottom: token('space.250'),
});

type OnboardingModalBodyProps = {
	selectedIndex: number;
	isLastSlide: boolean;
	isFirstSlide: boolean;
	slides: OnboardingSlide[];
	onPrev: () => void;
	onNext: () => void;
};

export function OnboardingModalBody({
	selectedIndex,
	isFirstSlide,
	isLastSlide,
	onPrev,
	onNext,
	slides,
}: OnboardingModalBodyProps) {
	const intl = useIntl();
	const { title, body, imageUrlLight, imageUrlDark, isTransparentBackground } =
		slides[selectedIndex];

	const renderChevrons = () => (
		<StyledFlexedButtons>
			<StyledButtonLeft>
				{!isFirstSlide && (
					<StyledChevronButton onClick={onPrev}>
						<ChevronLeftIcon
							label="previous-slide-chevron"
							color={token('color.icon.inverse')}
							size="small"
						/>
					</StyledChevronButton>
				)}
			</StyledButtonLeft>
			<StyledButtonRight>
				{!isLastSlide && (
					<StyledChevronButton onClick={onNext}>
						<ChevronRightIcon
							label="next-slide-chevron"
							color={token('color.icon.inverse')}
							size="small"
						/>
					</StyledChevronButton>
				)}
			</StyledButtonRight>
		</StyledFlexedButtons>
	);

	return (
		<Fragment>
			<StyledImageWrapper isTransparent={isTransparentBackground}>
				<ModalImage
					src={imageUrlLight}
					srcDark={imageUrlDark}
					alt={intl.formatMessage(i18n.imageAltText)}
				/>
				{renderChevrons()}
			</StyledImageWrapper>
			<StyledBody>
				<StyledHeading>{title}</StyledHeading>
				<div>{body}</div>
			</StyledBody>
		</Fragment>
	);
}
